// Copyright (C) 2021 Bystronic Laser AG

import axios from 'axios';
import { BaseUrl } from '@/models/constants';
import {
  StandardAlertListItem,
  StandardAlert,
  StandardAlertEligibleDevice,
} from '@/models/standardAlert';
import { responseHandler } from './response.handler';
import {
  StandardAlertCreationData,
  StandardAlertEditionData,
} from '@/components/alerts/model';

class StandardAlertsService {
  /** Returns a list of all standard alerts. */
  getAll(): Promise<StandardAlertListItem[]> {
    return axios
      .get<StandardAlertListItem[]>(`${BaseUrl}/standard-alerts`)
      .then((response) => responseHandler.handleDates(response.data));
  }

  /**
   * Returns a standard alert with all its fields.
   *
   * @param id Id of the standard alert to get.
   */
  getById(id: number): Promise<StandardAlert> {
    return axios
      .get<StandardAlert>(`${BaseUrl}/standard-alerts/${id}`)
      .then((response) => responseHandler.handleDates(response.data));
  }

  create(standardAlert: StandardAlertCreationData): Promise<void> {
    return axios.post(`${BaseUrl}/standard-alerts`, standardAlert);
  }

  async update(standardAlertData: StandardAlertEditionData): Promise<void> {
    return axios.put(`${BaseUrl}/standard-alerts`, standardAlertData);
  }

  async getEligibleDevices(
    standardAlerts: StandardAlertListItem[],
  ): Promise<StandardAlertEligibleDevice[]> {
    return await axios
      .get<StandardAlertEligibleDevice[]>(
        `${BaseUrl}/standard-alerts/eligible-devices`,
        {
          params: {
            ids: standardAlerts.map((x) => x.id),
          },
        },
      )
      .then((response) => response.data);
  }

  async apply(
    standardAlerts: StandardAlertListItem[],
    devices: StandardAlertEligibleDevice[],
  ): Promise<void> {
    await axios.put(`${BaseUrl}/standard-alerts/apply`, {
      standardAlertIds: standardAlerts.map((x) => x.id),
      deviceIds: devices.map((x) => x.id),
    });
  }
}

export const standardAlertsService = new StandardAlertsService();
